import { useRef, useState } from 'react'
import { Download, FileText, Trash2, Upload } from 'lucide-react'
import { deleteExpenseFile, expenseFileUrl, uploadExpenseFiles } from '../../api/operatingCosts'
import { fmtDay } from '../../utils/format'

// Bills and receipts attached to an expense. PDFs and photos only, as the
// server accepts; on a phone "image/*" also offers the camera.
export const FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,application/pdf,image/*'
const MAX_MB = 10
const OK_NAME = /\.(pdf|png|jpe?g|webp|gif|heic|heif)$/i

/** What's wrong with the chosen files before uploading, or ''. */
export function checkFiles(files) {
  for (const f of files) {
    if (!OK_NAME.test(f.name)) return `${f.name}: attach a PDF or a photo (JPG, PNG, WEBP or HEIC).`
    if (f.size > MAX_MB * 1024 * 1024) return `${f.name} is over ${MAX_MB} MB.`
  }
  return ''
}

export const fileSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

// Browsers can't show HEIC, so those get the plain file icon.
const showsInline = (type) => /^image\/(png|jpeg|webp|gif)$/.test(type || '')

/** The files on a saved expense: open, download, attach more, remove. */
export default function ExpenseFiles({ jeId, files, onChange, onError }) {
  const input = useRef(null)
  const [busy, setBusy] = useState('')

  const add = async (list) => {
    const chosen = [...(list || [])]
    if (input.current) input.current.value = ''
    if (!chosen.length) return
    const problem = checkFiles(chosen)
    if (problem) { onError(problem); return }
    setBusy('add')
    onError('')
    try {
      onChange(await uploadExpenseFiles(jeId, chosen))
    } catch (e) {
      onError(e.message || 'Could not attach the file.')
    } finally {
      setBusy('')
    }
  }

  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.file_name}?`)) return
    setBusy(`remove-${f.file_id}`)
    onError('')
    try {
      await deleteExpenseFile(f.file_id)
      onChange(files.filter((x) => x.file_id !== f.file_id))
    } catch (e) {
      onError(e.message || 'Could not remove the file.')
    } finally {
      setBusy('')
    }
  }

  return (
    <section className="doc-card">
      <div className="doc-card-head">
        <h2>Bill and receipts</h2>
        <span>{files.length ? `${files.length} attached` : `PDF or photo, up to ${MAX_MB} MB each`}</span>
      </div>
      {files.length ? (
        <ul className="exp-files">
          {files.map((f) => (
            <li key={f.file_id}>
              <a className="exp-file-thumb" href={expenseFileUrl(f.file_id)} target="_blank" rel="noreferrer" aria-label={`Open ${f.file_name}`}>
                {showsInline(f.content_type) ? <img src={expenseFileUrl(f.file_id)} alt="" loading="lazy" /> : <FileText size={22} aria-hidden="true" />}
              </a>
              <div className="exp-file-main">
                <a href={expenseFileUrl(f.file_id)} target="_blank" rel="noreferrer">{f.file_name}</a>
                <span>{fileSize(f.size_bytes)}{f.uploaded_at ? ` · added ${fmtDay(f.uploaded_at)}` : ''}</span>
              </div>
              <a className="ci-btn ci-btn-ghost ci-btn-sm" href={expenseFileUrl(f.file_id, { download: true })}><Download size={13} />Download</a>
              <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={!!busy} onClick={() => remove(f)} aria-label={`Remove ${f.file_name}`}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="doc-empty">No bill or receipt attached yet.</p>
      )}
      <div className="exp-files-add">
        <input ref={input} type="file" accept={FILE_ACCEPT} multiple hidden onChange={(e) => add(e.target.files)} />
        <button type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => input.current?.click()}>
          <Upload size={14} />{busy === 'add' ? 'Uploading…' : 'Attach a bill or receipt'}
        </button>
      </div>
    </section>
  )
}
